package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.domain.model.RmpAttachmentStatus
import br.com.vvrefeicoes.portal.domain.model.RmpLinkField
import br.com.vvrefeicoes.portal.domain.model.RmpOverview
import br.com.vvrefeicoes.portal.domain.model.RmpOverviewCount
import br.com.vvrefeicoes.portal.domain.model.RmpOverviewFilters
import br.com.vvrefeicoes.portal.domain.model.RmpOverviewItem
import br.com.vvrefeicoes.portal.domain.model.RmpOverviewSummary
import br.com.vvrefeicoes.portal.domain.model.RmpOverviewTotals
import br.com.vvrefeicoes.portal.domain.model.RmpOverviewView
import br.com.vvrefeicoes.portal.domain.model.RmpPortalOption
import br.com.vvrefeicoes.portal.domain.model.RmpRecord
import br.com.vvrefeicoes.portal.domain.model.RmpRecordAttachment
import br.com.vvrefeicoes.portal.domain.model.RmpReportKind
import br.com.vvrefeicoes.portal.domain.model.RmpSyncCell
import br.com.vvrefeicoes.portal.domain.model.RmpSyncPage
import br.com.vvrefeicoes.portal.domain.model.RmpSyncRecord
import br.com.vvrefeicoes.portal.domain.model.RmpSyncResult
import br.com.vvrefeicoes.portal.domain.model.RmpSyncSituation
import br.com.vvrefeicoes.portal.domain.model.RmpSyncStatusFilter
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.RmpPage
import br.com.vvrefeicoes.portal.domain.repository.RmpRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.client.statement.readRawBytes
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.encodeURLParameter
import io.ktor.http.encodeURLPathPart
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteRmpRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : RmpRepository {
    override suspend fun loadRecords(
        page: Int,
        size: Int,
        type: String?,
    ): RmpPage {
        val query =
            buildList {
                add("page=$page")
                add("size=$size")
                add("sort=rmpUpdatedAt,desc")
                type?.takeIf { it.isNotBlank() }?.let { add("tipo=${it.encodeURLParameter()}") }
            }.joinToString("&")
        val response: RmpPageDto<RmpRecordDto> = request { httpClient.get("$baseUrl/rmp/forms?$query") { authorize() } }.body()
        return RmpPage(
            records = response.content.map(RmpRecordDto::toDomain),
            totalPages = response.page.totalPages,
        )
    }

    override suspend fun loadOverview(
        query: String?,
        view: RmpOverviewView,
        myPending: Boolean,
        page: Int,
        size: Int,
        filters: RmpOverviewFilters,
    ): RmpOverview =
        request {
            httpClient.get("$baseUrl/rmp/overview?${overviewQuery(query, view, myPending, filters)}&page=$page&size=$size") {
                authorize()
            }
        }.body<RmpOverviewDto>().toDomain()

    override suspend fun exportOverview(
        query: String?,
        view: RmpOverviewView,
        myPending: Boolean,
        filters: RmpOverviewFilters,
    ): ByteArray =
        request {
            httpClient.get("$baseUrl/rmp/overview/export?${overviewQuery(query, view, myPending, filters)}") { authorize() }
        }.readRawBytes()

    override suspend fun exportReport(
        kind: RmpReportKind,
        query: String?,
        view: RmpOverviewView,
        myPending: Boolean,
        filters: RmpOverviewFilters,
    ): ByteArray =
        request {
            httpClient.get(
                "$baseUrl/rmp/overview/report?kind=${kind.name}&${overviewQuery(query, view, myPending, filters)}",
            ) { authorize() }
        }.readRawBytes()

    override suspend fun printOverview(
        query: String?,
        view: RmpOverviewView,
        myPending: Boolean,
        filters: RmpOverviewFilters,
    ): ByteArray =
        request {
            httpClient.get("$baseUrl/rmp/overview/print?${overviewQuery(query, view, myPending, filters)}") { authorize() }
        }.readRawBytes()

    private fun overviewQuery(
        query: String?,
        view: RmpOverviewView,
        myPending: Boolean,
        filters: RmpOverviewFilters,
    ): String =
        buildList {
            query?.trim()?.takeIf { it.isNotEmpty() }?.let { add("query=${it.encodeURLParameter()}") }
            add("view=${view.name}")
            add("myPending=$myPending")
            filters.type?.let { add("type=${it.encodeURLParameter()}") }
            filters.unit?.let { add("unit=${it.encodeURLParameter()}") }
            filters.from?.let { add("from=$it") }
            filters.to?.let { add("to=$it") }
            filters.stage?.let { add("stage=$it") }
        }.joinToString("&")

    override suspend fun sync(): RmpSyncResult = request { httpClient.post("$baseUrl/rmp/sync") { authorize() } }.body<RmpSyncDto>().toDomain()

    override suspend fun loadRecordAttachments(rmpRecordId: String): List<RmpRecordAttachment> =
        request { httpClient.get("$baseUrl/rmp/forms/${rmpRecordId.encodeURLPathPart()}/attachments") { authorize() } }
            .body<List<RmpRecordAttachmentDto>>()
            .map(RmpRecordAttachmentDto::toDomain)

    override suspend fun downloadRecordAttachment(
        rmpRecordId: String,
        attachmentId: String,
    ): ByteArray =
        request {
            httpClient.get(
                "$baseUrl/rmp/forms/${rmpRecordId.encodeURLPathPart()}/attachments/${attachmentId.encodeURLPathPart()}",
            ) { authorize() }
        }.readRawBytes()

    override suspend fun loadSyncRecords(
        page: Int,
        size: Int,
        type: String?,
        status: RmpSyncStatusFilter,
        situation: RmpSyncSituation,
        query: String?,
    ): RmpSyncPage {
        val params =
            buildList {
                type?.takeIf { it.isNotBlank() }?.let { add("tipo=${it.encodeURLParameter()}") }
                add("status=${status.name}")
                add("situacao=${situation.name}")
                add("page=$page")
                add("size=$size")
                query?.trim()?.takeIf { it.isNotEmpty() }?.let { add("q=${it.encodeURLParameter()}") }
            }.joinToString("&")
        return request { httpClient.get("$baseUrl/rmp/mappings/records?$params") { authorize() } }
            .body<RmpSyncPageDto>()
            .toDomain()
    }

    override suspend fun updateLink(
        rmpRecordId: String,
        field: RmpLinkField,
        targetId: String?,
    ) {
        request {
            httpClient.put("$baseUrl/rmp/mappings/records/${rmpRecordId.encodeURLPathPart()}/links/${field.name}") {
                jsonBody(RmpRecordLinkRequestDto(targetId))
            }
        }
    }

    override suspend fun updateLinks(
        rmpRecordId: String,
        links: Map<RmpLinkField, String>,
    ) {
        request {
            httpClient.put("$baseUrl/rmp/mappings/records/${rmpRecordId.encodeURLPathPart()}/links") {
                jsonBody(RmpRecordLinksRequestDto(links))
            }
        }
    }

    override suspend fun loadPortalUnits(): List<RmpPortalOption> =
        request { httpClient.get("$baseUrl/rmp/mappings/portal-units") { authorize() } }
            .body<List<RmpPortalOptionDto>>()
            .map(RmpPortalOptionDto::toDomain)

    override suspend fun loadPortalUsers(): List<RmpPortalOption> =
        request { httpClient.get("$baseUrl/rmp/mappings/portal-users") { authorize() } }
            .body<List<RmpPortalOptionDto>>()
            .map(RmpPortalOptionDto::toDomain)

    private suspend inline fun request(
        crossinline call: suspend () -> HttpResponse,
    ): HttpResponse {
        val response = call()
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<RmpApiErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
        return response
    }

    private fun HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }

    private fun HttpRequestBuilder.jsonBody(body: Any) {
        authorize()
        contentType(ContentType.Application.Json)
        setBody(body)
    }
}

@Serializable
private data class RmpSyncPageDto(
    val content: List<RmpSyncRecordDto> = emptyList(),
    val totalPages: Int = 1,
    val totalElements: Long = 0,
    val totalRmps: Long = 0,
    val rmpsPending: Long = 0,
    val cellsPending: Long = 0,
) {
    fun toDomain(): RmpSyncPage =
        RmpSyncPage(
            records = content.map(RmpSyncRecordDto::toDomain),
            totalPages = totalPages,
            totalElements = totalElements,
            totalRmps = totalRmps,
            rmpsPending = rmpsPending,
            cellsPending = cellsPending,
        )
}

@Serializable
private data class RmpSyncRecordDto(
    val id: String,
    val rmpId: Long,
    val tipo: String? = null,
    val status: String? = null,
    val createdAt: String? = null,
    val unit: RmpSyncCellDto,
    val requester: RmpSyncCellDto,
    val destinationDirector: RmpSyncCellDto,
    val director: RmpSyncCellDto,
    val generalDirector: RmpSyncCellDto,
    val hr: RmpSyncCellDto,
) {
    fun toDomain(): RmpSyncRecord =
        RmpSyncRecord(
            id = id,
            rmpId = rmpId,
            type = tipo,
            status = status,
            createdAt = createdAt,
            unit = unit.toDomain(),
            requester = requester.toDomain(),
            destinationDirector = destinationDirector.toDomain(),
            director = director.toDomain(),
            generalDirector = generalDirector.toDomain(),
            hr = hr.toDomain(),
        )
}

@Serializable
private data class RmpSyncCellDto(
    val field: RmpLinkField,
    val rmpName: String? = null,
    val rmpEmail: String? = null,
    val date: String? = null,
    val targetId: String? = null,
    val targetName: String? = null,
    val suggestedId: String? = null,
    val suggestedName: String? = null,
) {
    fun toDomain(): RmpSyncCell =
        RmpSyncCell(
            field = field,
            rmpName = rmpName,
            rmpEmail = rmpEmail,
            date = date,
            targetId = targetId,
            targetName = targetName,
            suggestedId = suggestedId,
            suggestedName = suggestedName,
        )
}

@Serializable
private data class RmpPortalOptionDto(
    val id: String,
    val name: String? = null,
    val detail: String? = null,
) {
    fun toDomain(): RmpPortalOption = RmpPortalOption(id = id, name = name.orEmpty(), detail = detail)
}

@Serializable
private data class RmpRecordLinkRequestDto(
    val targetId: String?,
)

@Serializable
private data class RmpRecordLinksRequestDto(
    val links: Map<RmpLinkField, String>,
)

@Serializable
private data class RmpPageDto<T>(
    val content: List<T> = emptyList(),
    val page: RmpPageMetadata = RmpPageMetadata(),
)

@Serializable
private data class RmpPageMetadata(
    val totalPages: Int = 1,
)

@Serializable
private data class RmpRecordDto(
    val id: String,
    val rmpId: Long,
    val tipo: String? = null,
    val status: String? = null,
    val statusRh: String? = null,
    val rmpUnitName: String? = null,
    val requesterName: String? = null,
    val requesterEmail: String? = null,
    val rmpCreatedAt: String? = null,
    val rmpUpdatedAt: String? = null,
    val lastSyncedAt: String? = null,
    val presentInRmp: Boolean = true,
) {
    fun toDomain(): RmpRecord =
        RmpRecord(
            id = id,
            rmpId = rmpId,
            type = tipo,
            status = status,
            hrStatus = statusRh,
            unitName = rmpUnitName,
            requesterName = requesterName,
            requesterEmail = requesterEmail,
            createdAt = rmpCreatedAt,
            updatedAt = rmpUpdatedAt,
            lastSyncedAt = lastSyncedAt,
            presentInRmp = presentInRmp,
        )
}

@Serializable
private data class RmpSyncDto(
    val total: Int = 0,
    val created: Int = 0,
    val updated: Int = 0,
    val failed: Int = 0,
    val deleted: Int = 0,
) {
    fun toDomain(): RmpSyncResult = RmpSyncResult(total = total, created = created, updated = updated, failed = failed, deleted = deleted)
}

@Serializable
private data class RmpRecordAttachmentDto(
    val id: String,
    val field: String = "",
    val fileName: String,
    val status: RmpAttachmentStatus = RmpAttachmentStatus.PENDENTE,
    val contentType: String? = null,
) {
    fun toDomain(): RmpRecordAttachment = RmpRecordAttachment(id = id, field = field, fileName = fileName, status = status, contentType = contentType)
}

@Serializable
private data class RmpApiErrorDto(
    val message: String? = null,
)

@Serializable
private data class RmpOverviewDto(
    val summary: RmpOverviewSummaryDto = RmpOverviewSummaryDto(),
    val items: List<RmpOverviewItemDto> = emptyList(),
    val totalElements: Long = 0,
    val totalPages: Int = 1,
    val requestsEnabled: Boolean = false,
    val totals: RmpOverviewTotalsDto = RmpOverviewTotalsDto(),
) {
    fun toDomain(): RmpOverview =
        RmpOverview(
            summary = RmpOverviewSummary(summary.total, summary.finished, summary.inProgress, summary.myPending),
            items = items.map(RmpOverviewItemDto::toDomain),
            totalPages = totalPages,
            totalElements = totalElements,
            requestsEnabled = requestsEnabled,
            totals = totals.toDomain(),
        )
}

@Serializable
private data class RmpOverviewCountDto(
    val key: String,
    val label: String? = null,
    val total: Long = 0,
) {
    fun toDomain(): RmpOverviewCount = RmpOverviewCount(key, label ?: key, total)
}

@Serializable
private data class RmpOverviewTotalsDto(
    val byStatus: List<RmpOverviewCountDto> = emptyList(),
    val byType: List<RmpOverviewCountDto> = emptyList(),
    val types: List<RmpOverviewCountDto> = emptyList(),
    val units: List<String> = emptyList(),
    val stages: List<RmpOverviewCountDto> = emptyList(),
) {
    fun toDomain(): RmpOverviewTotals =
        RmpOverviewTotals(
            byStatus = byStatus.map(RmpOverviewCountDto::toDomain),
            byType = byType.map(RmpOverviewCountDto::toDomain),
            types = types.map(RmpOverviewCountDto::toDomain),
            units = units,
            stages = stages.map(RmpOverviewCountDto::toDomain),
        )
}

@Serializable
private data class RmpOverviewSummaryDto(
    val total: Long = 0,
    val finished: Long = 0,
    val inProgress: Long = 0,
    val myPending: Long = 0,
)

@Serializable
private data class RmpOverviewItemDto(
    val id: String,
    val source: String = "RMP",
    val code: String,
    val type: String? = null,
    val typeLabel: String? = null,
    val status: String? = null,
    val statusLabel: String? = null,
    val requesterName: String? = null,
    val requesterEmail: String? = null,
    val unitName: String? = null,
    val waitingStageLabel: String? = null,
    val waitingName: String? = null,
    val myPending: Boolean = false,
    val createdAt: String? = null,
    val updatedAt: String? = null,
    val actorName: String? = null,
    val actorStageLabel: String? = null,
    val requesterRole: String? = null,
    val requiredAction: String? = null,
) {
    fun toDomain(): RmpOverviewItem =
        RmpOverviewItem(
            id = id,
            source = source,
            code = code,
            type = type,
            typeLabel = typeLabel,
            status = status,
            statusLabel = statusLabel,
            requesterName = requesterName,
            requesterEmail = requesterEmail,
            unitName = unitName,
            waitingStageLabel = waitingStageLabel,
            waitingName = waitingName,
            myPending = myPending,
            createdAt = createdAt,
            updatedAt = updatedAt,
            actorName = actorName,
            actorStageLabel = actorStageLabel,
            requesterRole = requesterRole,
            requiredAction = requiredAction,
        )
}
